import React, { useEffect, useRef, useState } from "react";
import { Search, CheckCircle2, Clock, ClipboardCheck, PackageCheck, Circle, FileText, ExternalLink, AlertTriangle, Upload, Send, Lock, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { t } from "../lib/i18n";
import { api, API } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const STATUS_STEPS = ["Received", "Documents Checking", "In Process", "Completed"];
const STATUS_ICON = {
  "Received": Circle,
  "Documents Checking": ClipboardCheck,
  "In Process": Clock,
  "Completed": CheckCircle2,
};

const STATUS_BADGE = {
  "Received":            "bg-slate-100 text-slate-800",
  "Documents Checking":  "bg-yellow-100 text-yellow-900",
  "In Process":          "bg-blue-100 text-blue-800",
  "Completed":           "bg-green-100 text-green-800",
  "Rejected":            "bg-red-100 text-red-800",
  "Transferred":         "bg-purple-100 text-purple-800",
  "Assigned":            "bg-indigo-100 text-indigo-800",
};

const TrackRequestPage = () => {
  const { tr } = useLang();
  const [reqId, setReqId] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!reqId.trim()) {
      toast.error(tr({ en: "Enter Application No.", hi: "आवेदन नंबर दर्ज करें" }));
      return;
    }
    setLoading(true);
    setData(null);
    try {
      const res = await api.post("/track", { request_id: reqId.trim().toUpperCase() });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr({ en: "Request not found", hi: "अनुरोध नहीं मिला" }));
    } finally {
      setLoading(false);
    }
  };

  const currentIdx = data ? (
    ["Transferred", "Assigned"].includes(data.status)
      ? STATUS_STEPS.indexOf("In Process")
      : STATUS_STEPS.indexOf(data.status)
  ) : -1;
  const trackMobile = data ? data.mobile : "";

  return (
    <div data-testid="track-page" className="max-w-3xl mx-auto px-4 lg:px-8 py-12 lg:py-14">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.14em] text-teal-700 font-semibold">{tr(t.nav.track)}</div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-heading font-semibold text-slate-900">{tr(t.track.title)}</h1>
        <p className="mt-2 text-slate-600">{tr(t.track.subtitle)}</p>
      </div>

      <form onSubmit={submit} className="rounded-xl bg-white border border-slate-200 p-6 sm:p-8">
        <div className="grid grid-cols-1 gap-4">
          <div>
            <Label className="text-sm font-medium text-slate-800">{tr({ en: "Application No. / Tracking ID", hi: "आवेदन नंबर / ट्रैकिंग आईडी" })}</Label>
            <Input value={reqId} onChange={(e) => setReqId(e.target.value)} placeholder={tr(t.track.reqIdPh)} className="mt-1.5 uppercase" data-testid="track-reqid-input" />
          </div>
        </div>
        <Button type="submit" disabled={loading} className="mt-5 h-11 bg-orange-600 hover:bg-orange-700 text-white" data-testid="track-check-btn">
          <Search className="w-4 h-4 mr-2" /> {loading ? tr(t.common.submitting) : tr(t.track.check)}
        </Button>
      </form>

      {data && (
        <div className="mt-8 rounded-xl bg-white border border-slate-200 p-6 sm:p-8 fade-up" data-testid="track-result">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{tr(t.common.reqId)}</div>
              <div className="mt-1 font-heading text-2xl font-semibold text-slate-900">{data.request_id}</div>
              <div className="mt-2 text-sm text-slate-600">
                {data.name} · {data.service_name}
                {data.custom_work && <span> · {data.custom_work}</span>}
              </div>
            </div>
            <Badge className={`${STATUS_BADGE[data.status]} rounded-full px-3 py-1`} data-testid="track-status-badge">
              {tr(t.status[data.status])}
            </Badge>
          </div>

          {/* Progress steps — only for non-terminal statuses */}
          {data.status !== "Rejected" && (
            <div className="mt-8 grid grid-cols-4 gap-2">
              {STATUS_STEPS.map((s, i) => {
                const Icon = STATUS_ICON[s];
                const done = i <= currentIdx;
                return (
                  <div key={s} className="text-center">
                    <div className={`mx-auto w-10 h-10 rounded-full flex items-center justify-center ${done ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-400"}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className={`mt-2 text-xs font-medium ${done ? "text-slate-900" : "text-slate-400"}`}>
                      {tr(t.status[s])}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <Info label={tr(t.common.created)} value={new Date(data.created_at).toLocaleString()} />
            <Info label={tr(t.common.updated)} value={new Date(data.updated_at).toLocaleString()} />
            <Info label={tr({ en: "Files uploaded", hi: "अपलोड फ़ाइलें" })} value={data.file_count} />
          </div>

          {data.admin_note && (
            <div className="mt-6 rounded-md bg-teal-50 border border-teal-100 p-4">
              <div className="text-xs uppercase tracking-widest text-teal-700 font-semibold mb-1">{tr({ en: "Note from Ashish Point", hi: "आशीष पॉइंट का नोट" })}</div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">{data.admin_note}</div>
            </div>
          )}

          {data.status === "Rejected" && data.reject_reason && (
            <div className="mt-6 rounded-md bg-red-50 border border-red-100 p-4">
              <div className="text-xs uppercase tracking-widest text-red-700 font-semibold mb-1">{tr({ en: "Reason for Rejection", hi: "अस्वीकृति का कारण" })}</div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">{data.reject_reason}</div>
            </div>
          )}

          {data.is_assigned && (
            <div className="mt-4 rounded-md bg-indigo-50 border border-indigo-100 p-4" data-testid="track-handled-by">
              <div className="text-xs uppercase tracking-widest text-indigo-700 font-semibold mb-1">{tr({ en: "Handled by", hi: "देखरेख" })}</div>
              <div className="text-sm text-slate-800">
                {tr({ en: "Assigned to an Ashish Point partner. You will receive updates here.", hi: "आशीष पॉइंट के पार्टनर को सौंपा गया। अपडेट यहीं मिलेंगे।" })}
                {data.deadline && <span className="ml-2 text-slate-500">· {tr({ en: "Deadline", hi: "समय-सीमा" })}: {data.deadline}</span>}
              </div>
            </div>
          )}

          {data.receipt_files && data.receipt_files.length > 0 && (
            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-green-700 font-semibold mb-2">{tr({ en: "Receipts / Proof of Work", hi: "रसीद / काम का प्रमाण" })}</div>
              <ul className="space-y-2">
                {data.receipt_files.map((f) => (
                  <li key={f.file_id} className="flex items-center justify-between rounded-md border border-green-200 bg-green-50/40 px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-green-700 shrink-0" />
                      <span className="text-sm text-slate-800 truncate">{f.original_filename}</span>
                    </div>
                    <a
                      href={`${API}/track/${data.request_id}/files/${f.file_id}?mobile=${encodeURIComponent(trackMobile)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-green-700 hover:underline text-xs font-medium"
                      data-testid={`track-receipt-${f.file_id}`}
                    >
                      {tr({ en: "Download", hi: "डाउनलोड" })} <ExternalLink className="w-3 h-3" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Need Info alert */}
          {data.need_info && data.need_info.active && (
            <NeedInfoBlock data={data} mobile={trackMobile} onDone={() => { /* reload */ document.querySelector('[data-testid="track-check-btn"]')?.click(); }} tr={tr} />
          )}

          {/* OTP verify */}
          {data.otp_status && !data.otp_status.verified && (
            <OtpBlock data={data} mobile={trackMobile} onDone={() => document.querySelector('[data-testid="track-check-btn"]')?.click()} tr={tr} />
          )}
          {data.otp_status?.verified && (
            <div className="mt-6 rounded-md bg-green-50 border border-green-200 p-4 text-sm text-green-900 flex items-center gap-2">
              <Lock className="w-4 h-4" /> {tr({ en: "Consent OTP verified successfully.", hi: "सहमति OTP सफलतापूर्वक सत्यापित।" })}
            </div>
          )}

          {/* Live Chat */}
          <ChatBlock data={data} mobile={trackMobile} tr={tr} />
        </div>
      )}
    </div>
  );
};

const NeedInfoBlock = ({ data, mobile, onDone, tr }) => {
  const ni = data.need_info;
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const inp = useRef(null);

  const submit = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("request_id", data.request_id);
      fd.append("mobile", mobile);
      if (Object.keys(answers).length) fd.append("answers", JSON.stringify(answers));
      files.forEach((f) => fd.append("files", f));
      await api.post("/track/need-info/fill", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(tr({ en: "Information sent — thank you!", hi: "जानकारी भेजी गयी — धन्यवाद!" }));
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-6 rounded-md bg-red-50 border border-red-300 p-4 animate-pulse-slow" data-testid="track-need-info">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-red-800">{tr({ en: "Additional info required from you", hi: "आपसे अतिरिक्त जानकारी चाहिए" })}</div>
          <div className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{ni.message}</div>
          {(ni.fields || []).map((f) => (
            <div key={f.key} className="mt-3">
              <Label className="text-xs">{f.label}</Label>
              <Input value={answers[f.key] || ""} onChange={(e) => setAnswers({ ...answers, [f.key]: e.target.value })} data-testid={`ni-answer-${f.key}`} />
            </div>
          ))}
          <div className="mt-3">
            <Button variant="outline" size="sm" onClick={() => inp.current?.click()} data-testid="ni-upload-btn"><Upload className="w-3 h-3 mr-1" /> {tr({ en: "Attach files", hi: "फ़ाइल जोड़ें" })}</Button>
            <input ref={inp} type="file" multiple accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
            {files.length > 0 && <span className="ml-2 text-xs text-slate-600">{files.length} file(s) selected</span>}
          </div>
          <Button onClick={submit} disabled={saving} className="mt-3 bg-red-600 hover:bg-red-700 text-white" data-testid="ni-submit-btn"><Send className="w-4 h-4 mr-2" /> {saving ? "Sending…" : tr({ en: "Send Info", hi: "जानकारी भेजें" })}</Button>
        </div>
      </div>
    </div>
  );
};

const OtpBlock = ({ data, mobile, onDone, tr }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const verify = async () => {
    if (code.length !== 6) { toast.error("Enter 6 digits"); return; }
    setBusy(true);
    try {
      await api.post("/track/otp/verify", { request_id: data.request_id, mobile, code });
      toast.success(tr({ en: "OTP verified", hi: "OTP सत्यापित" }));
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-6 rounded-md bg-amber-50 border border-amber-300 p-4" data-testid="track-otp-block">
      <div className="flex items-start gap-2">
        <Lock className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-amber-900">{tr({ en: "OTP requested — please confirm your consent", hi: "OTP माँगा गया — कृपया सहमति दें" })}</div>
          <p className="text-xs text-slate-600 mt-1">{tr({ en: "Our team has generated a 6-digit code. Ask Ashish Point staff for the code and enter it below.", hi: "हमारी टीम ने 6-अंकीय कोड बनाया है। आशीष पॉइंट स्टाफ से कोड पूछें और नीचे दर्ज करें।" })}</p>
          <div className="flex gap-2 mt-3">
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" className="w-32 tracking-widest text-center font-mono text-lg" data-testid="otp-code-input" />
            <Button onClick={verify} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="otp-verify-btn">{busy ? "…" : tr({ en: "Verify", hi: "सत्यापित करें" })}</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ChatBlock = ({ data, mobile, tr }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post("/track/chat/send", { request_id: data.request_id, mobile, text: text.trim() });
      toast.success(tr({ en: "Message sent", hi: "संदेश भेजा गया" }));
      setText("");
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-6 rounded-lg border border-slate-200 p-4" data-testid="track-chat">
      <div className="flex items-center gap-2 mb-2">
        <MessageCircle className="w-4 h-4 text-teal-700" />
        <div className="text-sm font-semibold">{tr({ en: "Send a message to Ashish Point", hi: "आशीष पॉइंट को संदेश भेजें" })}</div>
      </div>
      <p className="text-xs text-slate-500 mb-3">{tr({ en: "Type your query and press Send. For privacy, past messages are not shown here — our team receives them on the workspace.", hi: "अपना सवाल लिखिये और Send दबाइये। गोपनीयता के लिए पुराने संदेश यहाँ नहीं दिखते — हमारी टीम को वे वर्कस्पेस में मिलते हैं।" })}</p>
      <div className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={tr({ en: "Type a message…", hi: "संदेश लिखें…" })} data-testid="track-chat-input" />
        <Button onClick={send} disabled={busy} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="track-chat-send"><Send className="w-4 h-4 mr-1" /> {tr({ en: "Send", hi: "भेजें" })}</Button>
      </div>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">{label}</div>
    <div className="mt-1 text-slate-800">{value}</div>
  </div>
);

export default TrackRequestPage;
